/**
 * Compact Channel Filter Component
 * For use in sticky header - compact version of channel filter
 */
import { getChannelColorVar } from '../../../utils/channelColorUtils.js';

export default function CompactChannelFilter({ 
  channels = [], 
  selectedChannels = [], 
  onChannelsChange,
  /** When true, use larger tap targets (min 44px) for use in mobile sheets */
  touchFriendly = false,
}) {
  const handleChannelToggle = (channelCode) => {
    if (!onChannelsChange) return;
    if (selectedChannels.includes(channelCode)) {
      onChannelsChange(selectedChannels.filter(c => c !== channelCode));
    } else {
      onChannelsChange([...selectedChannels, channelCode]);
    }
  };

  const buttonStyle = touchFriendly ? { ...styles.button, ...styles.buttonTouchFriendly } : styles.button;
  const containerStyle = touchFriendly ? { ...styles.container, ...styles.containerTouchFriendly } : styles.container;
  const buttonsStyle = touchFriendly ? { ...styles.buttons, ...styles.buttonsTouchFriendly } : styles.buttons;

  return (
    <div style={containerStyle}>
      <div style={styles.label}>Channel:</div>
      <div style={buttonsStyle}>
        <button
          onClick={() => onChannelsChange?.([])}
          style={{
            ...buttonStyle,
            ...(selectedChannels.length === 0 ? styles.buttonActive : {}),
          }}
          aria-pressed={selectedChannels.length === 0}
        >
          All
        </button>
        {channels.map((channel) => {
          const isSelected = selectedChannels.includes(channel.code);
          return (
            <button
              key={channel.code}
              onClick={() => handleChannelToggle(channel.code)}
              style={{
                ...buttonStyle,
                ...(isSelected ? styles.buttonActive : {}),
                // Use shared strict resolver for colors (single source of truth) - do not use backend color_hex
                borderLeft: `2px solid ${getChannelColorVar(channel.code || channel.master_channel_key)}`,
              }}
              aria-pressed={isSelected}
            >
              {channel.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  label: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--color-text-secondary, #404040)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  buttons: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  button: {
    padding: '4px 12px',
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    border: '1px solid var(--color-border-medium, #d1d5db)',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    color: 'var(--color-text-primary, #374151)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    minHeight: '28px',
  },
  buttonActive: {
    backgroundColor: 'var(--color-primary-base, #0f4c75)',
    color: '#ffffff',
    borderColor: 'var(--color-primary-base, #0f4c75)',
  },
  containerTouchFriendly: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  buttonsTouchFriendly: {
    flexDirection: 'column',
    gap: 'var(--spacing-2)',
  },
  buttonTouchFriendly: {
    minHeight: '44px',
    padding: '12px 16px',
    fontSize: 'var(--font-size-base, 14px)',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
  },
};
